import React from 'react';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';

import messages from './messages/Schema';

const parseDate = dateString => {
  if (moment(dateString, 'l').isValid()) {
    return moment(dateString, 'l');
  } else if (moment(dateString).isValid()) {
    return moment(dateString);
  }
  return null;
}
const dateToPrintable = d => {
  const momentDate = parseDate(d);
  if (!momentDate) {
    return null;
  }
  return momentDate.format('l');
}
const dateToComparable = d => {
  const momentDate = parseDate(d);
  return momentDate;
}
const matchDates = (a, b) => {
  const dateA = dateToComparable(a);
  const dateB = dateToComparable(b);
  if (!dateA || !dateB) return false;
  return dateA.isSame(dateB);
}

const parseString = string => {
  if (typeof string === 'string') {
    return string;
  }
  try {
    return string.toString();
  } catch (exception) {
    return '';
  }
}
const stringToPrintable = s => {
  const string = parseString(s);
  return string;
}
const stringToComparable = s => {
  const string = parseString(s);
  return string.toLowerCase();
}
const matchStrings = (a, b) => {
  const stringA = stringToComparable(a);
  const stringB = stringToComparable(b);
  if (!stringA || !stringB) return false;
  return stringA.indexOf(stringB) !== -1;
}

const parseMoney = money => isNaN(parseFloat(money)) ? 0 : parseFloat(money);
const moneyToPrintable = money => `${parseMoney(money)}€`;
const moneyToComparable = money => parseMoney(money);
const matchMoney = (a, b) => moneyToComparable(a) === moneyToComparable(b);

const enumToPrintable = (prop, val) =>
  <FormattedMessage { ...messages[`${prop}.${val}`] } />;

class Base {
  constructor(data, schema) {
    this.data = data;
    this.schema = schema;
  }
  get id() {
    return this.data.id;
  }
  get(property) {
    // property is a field name in dot-notation and the reduce traverses the
    // license data as denoted by property.
    // i.e. 'field.sub' -> l['field']['sub']
    return property.split('.').reduce((o, i) => o[i], this);
  }
  toPrintable(property) {
    const meta = this.schema[property];
    const value = this.get(property);
    return meta.toPrintable(value);
  }
  toComparable(property) {
    const meta = this.schema[property];
    const value = this.get(property);
    return meta.toComparable(value);
  }
}
class Log extends Base {
  constructor(data, license) {
    super(data, Log.schema);
    this.licenseLog = this.data;
    this._license = license;
  }
  static get schema() {
    return {
      'licenseLog.id': {
        key: 'licenseLog.id',
        toComparable: dateToComparable,
        toPrintable: dateToPrintable,
        match: matchDates,
      },
      'licenseLog.licenseId': {
        key: 'licenseLog.licenseId',
        toComparable: dateToComparable,
        toPrintable: dateToPrintable,
        match: matchDates,
      },
      'licenseLog.createdAt': {
        key: 'licenseLog.createdAt',
        toComparable: dateToComparable,
        toPrintable: dateToPrintable,
        type: 'date',
        match: matchDates,
      },
      'licenseLog.billedAt': {
        key: 'licenseLog.billedAt',
        toComparable: dateToComparable,
        toPrintable: dateToPrintable,
        type: 'date',
        match: matchDates,
      },
      'licenseLog.endDate': {
        key: 'licenseLog.endDate',
        toComparable: dateToComparable,
        toPrintable: dateToPrintable,
        type: 'date',
        match: matchDates,
      },
      'licenseLog.extraMonths': {
        key: 'licenseLog.extraMonths',
        toComparable: parseInt,
        toPrintable: parseInt,
        match: matchStrings,
      },
      'licenseLog.paidAt': {
        key: 'licenseLog.paidAt',
        toComparable: dateToComparable,
        toPrintable: dateToPrintable,
        type: 'date',
        match: matchDates,
      },
      'licenseLog.priceNoVat': {
        key: 'licenseLog.priceNoVat',
        toComparable: moneyToComparable,
        toPrintable: moneyToPrintable,
        match: matchMoney,
      },
      'licenseLog.startDate': {
        key: 'licenseLog.startDate',
        toComparable: dateToComparable,
        toPrintable: dateToPrintable,
        type: 'date',
        match: matchDates,
      },
      'licenseLog.status': {
        key: 'licenseLog.status',
        toComparable: stringToComparable,
        toPrintable: value => enumToPrintable('status', value),
        match: matchStrings,
        type: 'enum',
      },
      'licenseLog.customerReference': {
        key: 'licenseLog.customerReference',
        toComparable: stringToComparable,
        toPrintable: stringToPrintable,
        match: matchStrings,
        type: 'text',
      },
      'company.businessId': {
        key: 'company.businessId',
        toComparable: stringToComparable,
        toPrintable: stringToPrintable,
        match: matchStrings,
        type: 'text',
      },
      'company.name': {
        key: 'company.name',
        toComparable: stringToComparable,
        toPrintable: stringToPrintable,
        match: matchStrings,
        type: 'text',
      },
      'licenseType.name': {
        key: 'licenseType.name',
        toComparable: stringToComparable,
        toPrintable: stringToPrintable,
        match: matchStrings,
        type: 'text',
      },
    };
  }
  get licenseId() {
    return this.data.licenseId;
  }
  get createdAt() {
    return dateToPrintable(this.data.createdAt);
  }
  get priceNoVat() {
    return moneyToPrintable(this.data.priceNoVat);
  }
  get startDate() {
    return dateToPrintable(this.data.startDate);
  }
  get endDate() {
    return dateToPrintable(this.data.endDate);
  }
  get extraMonths() {
    return this.data.extraMonths;
  }
  get billedAt() {
    return dateToPrintable(this.data.billedAt);
  }
  get paidAt() {
    return dateToPrintable(this.data.paidAt);
  }
  get description() {
    return stringToPrintable(this.data.description);
  }
  get status() {
    return this.data.status;
  }
  get customerReference() {
    return this.data.customerReference;
  }
  set customerReference(customerReference) {
    this.data.customerReference = customerReference;
  }
  get license() {
    return this._license;
  }
  get company() {
    if (this._license) {
      return this._license.company;
    } else if (this.data.company) {
      return this.data.company;
    }
    return {};
  }
  get licenseType() {
    if (this._license) {
      return this._license.licenseType;
    } else if (this.data.licenseType) {
      return this.data.licenseType;
    }
    return {};
  }
  clone() {
    const data = JSON.parse(JSON.stringify(this.data));
    return new Log(data, this._license);
  }
}
class License extends Base {
  constructor(data) {
    super(data, License.schema);
    this.license = this.data;
    this.license.rrpNoVat = this.rrpNoVat;
    this._log = data.log
      ? data.log.map(l => new Log(l, this))
      : [];
    this.timestamp = Date.now();
  }
  static get schema() {
    return {
      'license.status': {
        key: 'license.status',
        toComparable: stringToComparable,
        toPrintable: value => enumToPrintable('status', value),
        match: matchStrings,
        type: 'enum',
      },
      'license.createdAt': {
        key: 'license.createdAt',
        toComparable: dateToComparable,
        toPrintable: dateToPrintable,
        type: 'date',
        match: matchDates,
      },
      'company.businessId': {
        key: 'company.businessId',
        toComparable: stringToComparable,
        toPrintable: stringToPrintable,
        match: matchStrings,
        type: 'text',
      },
      'company.name': {
        key: 'company.name',
        toComparable: stringToComparable,
        toPrintable: stringToPrintable,
        match: matchStrings,
        type: 'text',
      },
      'license.deactivatedAt': {
        key: 'license.deactivatedAt',
        toComparable: dateToComparable,
        toPrintable: dateToPrintable,
        type: 'date',
        match: matchDates,
      },
      'deactivatedBy.id': {
        key: 'deactivatedBy.id',
        toComparable: stringToComparable,
        toPrintable: stringToPrintable,
        match: matchStrings,
      },
      'deactivatedBy.username': {
        key: 'deactivatedBy.username',
        toComparable: stringToComparable,
        toPrintable: stringToPrintable,
        match: matchStrings,
        type: 'text',
      },
      'license.endDate': {
        key: 'license.endDate',
        toComparable: dateToComparable,
        toPrintable: dateToPrintable,
        type: 'date',
        match: matchDates,
      },
      'license.extraMonths': {
        key: 'license.extraMonths',
        toComparable: parseInt,
        toPrintable: parseInt,
        match: matchStrings,
      },
      'licenseType.name': {
        key: 'licenseType.name',
        toComparable: stringToComparable,
        toPrintable: stringToPrintable,
        match: matchStrings,
        type: 'text',
      },
      'licenseType.periodInMonths': {
        key: 'licenseType.periodInMonths',
        toComparable: stringToComparable,
        toPrintable: stringToPrintable,
        match: matchStrings,
      },
      'licenseType.rrpNoVat': {
        key: 'licenseType.rrpNoVat',
        toComparable: moneyToComparable,
        toPrintable: moneyToPrintable,
        match: matchMoney,
      },
      'licenseType.billingBackend': {
        key: 'licenseType.billingBackend',
        toComparable: stringToComparable,
        toPrintable: stringToComparable,
        match: matchStrings,
      },
      'seller.id': {
        key: 'seller.id',
        toComparable: stringToComparable,
        toPrintable: stringToPrintable,
        match: matchStrings,
      },
      'seller.username': {
        key: 'seller.username',
        toComparable: stringToComparable,
        toPrintable: stringToPrintable,
        match: matchStrings,
        type: 'text',
      },
      'seller.email': {
        key: 'seller.email',
        toComparable: stringToComparable,
        toPrintable: stringToPrintable,
        match: matchStrings,
        type: 'text',
      },
      'retailer.id': {
        key: 'retailer.id',
        toComparable: stringToComparable,
        toPrintable: stringToPrintable,
        match: matchStrings,
      },
      'retailer.name': {
        key: 'retailer.name',
        toComparable: stringToComparable,
        toPrintable: stringToPrintable,
        match: matchStrings,
        type: 'text',
      },
      'retailer.countryCode': {
        key: 'retailer.countryCode',
        toComparable: stringToComparable,
        toPrintable: stringToPrintable,
        match: matchStrings,
      },
      'retailer.businessId': {
        key: 'retailer.name',
        toComparable: stringToComparable,
        toPrintable: stringToPrintable,
        match: matchStrings,
        type: 'text',
      },
      'license.startDate': {
        key: 'license.startDate',
        toComparable: dateToComparable,
        toPrintable: dateToPrintable,
        type: 'date',
        match: matchDates,
      },
      'license.continuous': {
        key: 'license.continuous',
        toComparable: val => val,
        toPrintable: value => enumToPrintable('continuous', value),
        match: (a, b) => a === b,
        type: 'enum',
      },
      'license.customerReference': {
        key: 'license.customerReference',
        toComparable: stringToComparable,
        toPrintable: stringToPrintable,
        match: matchStrings,
        type: 'text',
      },
      'contactPerson.firstName': {
        key: 'contactPerson.firstName',
        toComparable: stringToComparable,
        toPrintable: stringToPrintable,
        match: matchStrings,
        type: 'text',
      },
      'contactPerson.lastName': {
        key: 'contactPerson.firstName',
        toComparable: stringToComparable,
        toPrintable: stringToPrintable,
        match: matchStrings,
        type: 'text',
      },
      'contactPerson.name': {
        key: 'contactPerson.name',
        toComparable: stringToComparable,
        toPrintable: stringToPrintable,
        match: matchStrings,
      },
      'contactPerson.email': {
        key: 'contactPerson.email',
        toComparable: stringToComparable,
        toPrintable: stringToPrintable,
        match: matchStrings,
        type: 'text',
      },
      'contactPerson.phone': {
        key: 'contactPerson.phone',
        toComparable: stringToComparable,
        toPrintable: stringToPrintable,
        match: matchStrings,
        type: 'text',
      },
      'createdBy.id': {
        key: 'createdBy.id',
        toComparable: stringToComparable,
        toPrintable: stringToPrintable,
        match: matchStrings,
      },
      'createdBy.username': {
        key: 'createdBy.username',
        toComparable: stringToComparable,
        toPrintable: stringToPrintable,
        match: matchStrings,
        type: 'text',
      },
      'license.priceNoVat': {
        key: 'license.priceNoVat',
        toComparable: moneyToComparable,
        toPrintable: moneyToPrintable,
        match: matchMoney,
      },
    };
  }
  get status() {
    return enumToPrintable('status', this.data.status);
  }
  get company() {
    return this.data.company;
  }
  get licenseType() {
    return {
      id: this.data.licenseType.id,
      name: stringToPrintable(this.data.licenseType.name),
      billingBackend: this.data.licenseType.billingBackend,
      rrpNoVat: moneyToPrintable(this.data.licenseType.rrpNoVat),
      minPriceNoVat: moneyToPrintable(this.data.licenseType.minPriceNoVat),
      maxPriceNoVat: moneyToPrintable(this.data.licenseType.maxPriceNoVat),
      periodInMonths: stringToPrintable(this.data.licenseType.periodInMonths),
      extraMonths: this.data.licenseType.extraMonths,
      active: this.data.licenseType.active,
    };
  }
  get billingBackend() {
    return this.licenseType.billingBackend;
  }
  get continuous() {
    return enumToPrintable('continuous', this.data.continuous);
  }
  toggleContinuous() {
    this.data.continuous = !this.data.continuous;
  }
  get createdAt() {
    return dateToPrintable(this.data.createdAt);
  }
  get seller() {
    return this.data.seller;
  }
  get retailer() {
    return this.data.retailer;
  }
  get deactivatedAt() {
    return dateToPrintable(this.data.deactivatedAt);
  }
  get deactivatedBy() {
    return {
      id: 123,
      username: '',
    };
  }
  get log() {
    return this._log;
  }
  getLogsByFilter = predicate => this.log.filter(predicate);
  orderLogsByDate = (logs, date, order) =>
    logs.sort((a, b) => {
      const aComp = dateToComparable(a.data[date]);
      const bComp = dateToComparable(b.data[date]);
      if (aComp > bComp) return -order;
      if (aComp < bComp) return order;
      return 0;
    });
  get oldestLog() {
    // -1 as in ascending order
    return this.orderLogsByDate(this.log, 'startDate', -1)[0];
  }
  get newestLog() {
    /* Get the newest log for the license regardless of its status.
     */
    if (!this.log.length) {
      return null;
    }
    // 1 as in descending order
    return this.orderLogsByDate(this.log, 'startDate', 1)[0];
  }
  get currentLog() {
    /* Get the current log in state 'invoiced' for the license.
     */
    if (!this.log.length) {
      return null;
    }
    // 'none'-type payments (e.g. 0 cost licenses), are left in status 'open'.
    const logs = this.getLogsByFilter(
        l => ['invoiced', 'open'].indexOf(l.status) !== -1
    );
    if (!logs.length) {
      return null;
    }
    return logs[0];
  }
  get startDate() {
    return dateToPrintable(this.data.startDate);
  }
  get endDate() {
    return dateToPrintable(this.data.endDate);
  }
  get extraMonths() {
    return this.data.extraMonths;
  }
  get customerReference() {
    return this.data.customerReference;
  }
  set customerReference(value) {
    this.data.customerReference = value;
  }
  get rrpNoVat() {
    return this.licenseType.rrpNoVat;
  }
  get priceNoVat() {
    const currentLog = this.currentLog;
    if (currentLog) {
      return currentLog.priceNoVat;
    }
    const newestLog = this.newestLog;
    if (newestLog) {
      return newestLog.priceNoVat;
    }
    return this.rrpNoVat;
  }
  get contactPerson() {
    const { contactPerson } = this.data;
    const { firstName, lastName } = contactPerson;
    return {
      ...contactPerson,
      name: (firstName && lastName) ? `${firstName} ${lastName}` : '',
    };
  }
  get createdBy() {
    return this.data.createdBy;
  }
  clone() {
    const data = JSON.parse(JSON.stringify(this.data));
    return new License(data);
  }
};

export {
  Log,
  License,
};
